import React, { useState, useCallback } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import DragAndDrop from '../../components/DragAndDrop/DragAndDrop';
import FileUploadCard from '../../components/FileUploadCard/FileUploadCard';
import VendorSelection from '../../components/VendorSelection/VendorSelection';
import {
  PageContainer,
  ContentContainer,
  FullHeightCardWrapper,
} from './UploadProductContainer.styles';
import { uploadAndStage, listUploadLogs } from '../../api/api';

// Vendor example data
const vendorExamples = {
  'sourcing-it': {
    headers: ['Brand', 'Product Name', 'Product Description', 'Long Description', 'Part Number', 'Condition', 'Cost Price', 'Recommended Retail Price', 'Pack Weight', 'Pack Dimension', 'Product Location', 'No. of Stock/Units Available'],
    example: ['Dell', 'Hard Drive', '3.84TB 2.5" SAS SSD', 'Warranty: 60 Days', 'N85XX', 'New', '125.00', '150.00', 'Not Available', 'Not Available', 'UK', '50'],
    notes: [
      'Product Name column will be saved to the category field in the database',
      'Long Description column will be saved to the specifications field',
      'Concatenate code will be auto-generated as: {vendor-name}-{Part Number}-{Condition}',
      'Recommended Retail Price is optional - if not provided, it will be calculated as Cost Price + 15%'
    ]
  },
  'mfp-technology': {
    headers: ['MFR', 'PART #', 'CC', 'INTERNAL PART #', 'LOCATION', 'SUB P/N', 'SUB 2 P/N', 'Description', 'Long Description', 'Category', 'Condition', 'QTY', 'Lenovo $'],
    example: ['LEN', '0000000AG562', '12', '0000000AG562-12-HU', 'US', '00AG560', '00AG562', 'LENOVO EMULEX VFA5.2 ML2 DUAL PORT 10GB ETHER', 'Warranty: 60 Days', 'Adapter - Network', 'New', '1', '119'],
    notes: [
      'CC and INTERNAL PART # columns are ignored during upload',
      'Long Description column will be saved to the specifications field',
      'If Lenovo $ is "Call", the product will be marked as "Price on Demand"',
      'If Lenovo $ is numeric, retail price will be calculated as Lenovo $ + 15%',
      'Concatenate code will be auto-generated as: {vendor-name}-{PART #}-{Condition}',
      'SUB P/N and SUB 2 P/N are optional fields'
    ]
  },
  'lenovo-top-choice': {
    headers: ['Vendor', 'Part No', 'Name', 'Long Description', 'Product Category', 'Qty', 'Unit Cost Price ($)'],
    example: ['LEN', '7DCE100EEA', 'ThinkSystem ST250 V3 6353P 32GB', 'Proc - Intel, Xeon E-2378, 8 Cores, 65W, 2.6GHz x1; Mem - 32GB TruDDR4...', 'Server', '25', '2884'],
    notes: [
      'Vendor column is ignored - vendor is set from the selection',
      'Long Description will be saved to the specifications field',
      'Brand is automatically set to "Lenovo", Condition to "New", and Location to "Netherlands"',
      'Retail price is automatically calculated as Unit Cost Price + 15%',
      'Concatenate code will be auto-generated as: {vendor-name}-{Part No}-New'
    ]
  },
  'lcr': {
    headers: ['Vendor', 'Part No', 'Name', 'Long Description', 'Catego', 'Qty', 'Unit Cost Price ($)'],
    example: ['LCR', '7D7QA031EA', 'ThinkSystem SR250 V2 E-2378 32GB', 'Proc - Intel, Xeon E-2378, 8 Cores, 65W, 2.6GHz x1; Mem - 32GB TruDDR4...', 'Server', '4', '1166.26'],
    notes: [
      'Vendor column is ignored - vendor is set from the selection',
      'Long Description column will be saved to the specifications field',
      'Brand will be set to "Lenovo" automatically',
      'Condition will be set to "Refurbished" automatically',
      'Location will be set to "Netherlands" automatically',
      'Retail price will be calculated as Unit Cost Price + 15%',
      'Concatenate code will be auto-generated as: {vendor-name}-{Part No}-{Condition}'
    ]
  }
};

export default function UploadProductContainer({ activeTab, onUploadComplete }) {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [catalogUploadMode, setCatalogUploadMode] = useState('partial');
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showErrorBox, setShowErrorBox] = useState(false);

  const handleFileUpload = (files) => {
    if (!selectedVendor) {
      setMessage('Please select a vendor first.');
      return;
    }
    const newFiles = Array.isArray(files) ? files : [files];
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setMessage('');
    // Clear errors when new file is uploaded
    setUploadErrors([]);
    setShowErrorBox(false);
  };

  const handleRemoveFile = (fileIndex) => {
    setUploadedFiles((prev) => prev.filter((_, index) => index !== fileIndex));
    // Clear errors when file is removed
    setUploadErrors([]);
    setShowErrorBox(false);
  };

  const handleCompleteUpload = useCallback(async () => {
    if (uploadedFiles.length === 0) {
      setMessage('Please add at least one CSV/XLSX file.');
      return;
    }

    setBusy(true);
    setMessage('');

    let lastLogId = null;
    let hasErrors = false;
    let validationErrors = [];

    try {

      for (const f of uploadedFiles) {
        if (!(f instanceof File)) {
          console.warn('Skipping non-File entry:', f);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f, f.name || 'upload.csv');
        if (selectedVendor) {
          fd.append('vendor', selectedVendor);
        }
        fd.append('catalog_upload_mode', catalogUploadMode);
        try {
          const res = await uploadAndStage(fd);
          if (res && (res.upload_log_id || res.id)) {
            lastLogId = res.upload_log_id || res.id;
          }
        } catch (error) {
          console.log('Upload error caught:', error);
          console.log('Error status:', error?.status);
          console.log('Error data:', error?.data);
          console.log('Error text:', error?.text);
          
          // Check if error has validation errors (from apiRequest)
          // Handle both 422 and 400 errors
          if (error?.status === 422 || error?.status === 400) {
            // Check for errors array format (422 - standard format)
            if (error?.data?.detail?.errors && Array.isArray(error.data.detail.errors)) {
              validationErrors = error.data.detail.errors;
              hasErrors = true;
              break;
            }
            // Check for direct array format (400 - sometimes detail is directly an array)
            else if (error?.data?.detail && Array.isArray(error.data.detail)) {
              // Convert array format to errors format
              validationErrors = error.data.detail.map(err => ({
                line: err.line || 'Unknown',
                field: err.field || 'Unknown Field',
                message: err.message || `Missing required columns: ${err.field || 'Unknown'}`,
                type: err.type || 'Missing Field'
              }));
              hasErrors = true;
              break;
            }
            // Check if error.text contains JSON that needs parsing
            else if (error?.text) {
              try {
                const parsedText = JSON.parse(error.text);
                // Check if parsed text has the detail.errors structure
                if (parsedText?.detail?.errors && Array.isArray(parsedText.detail.errors)) {
                  validationErrors = parsedText.detail.errors;
                  hasErrors = true;
                  break;
                }
                // Check if parsed text is directly an array
                else if (Array.isArray(parsedText)) {
                  validationErrors = parsedText.map(err => ({
                    line: err.line || 'Unknown',
                    field: err.field || 'Unknown Field',
                    message: err.message || `Missing required columns: ${err.field || 'Unknown'}`,
                    type: err.type || 'Missing Field'
                  }));
                  hasErrors = true;
                  break;
                }
              } catch (e) {
                console.log('Failed to parse error text as JSON:', e);
                // Not JSON, continue to throw
              }
            }
          }
          
          // If we get here and it's a 422/400 but we didn't extract errors, log it
          if ((error?.status === 422 || error?.status === 400) && !hasErrors) {
            console.error('422/400 error but could not extract validation errors:', error);
          }
          
          // Other errors - rethrow
          throw error;
        }
      }

      // Handle errors
      if (hasErrors) {
        setUploadErrors(validationErrors);
        setShowErrorBox(true);
        setMessage(`Upload failed with ${validationErrors.length} error(s). Please check the errors below.`);
        // DO NOT call onUploadComplete if there are errors
        return;
      }

      // Only proceed if no errors
      if (!lastLogId) {
        try {
          const logs = await listUploadLogs();
          if (Array.isArray(logs) && logs.length > 0) {
            const latest = logs[0];
            lastLogId = latest.id || latest.upload_log_id || null;
          }
        } catch (_e) {}
      }

      if (onUploadComplete && lastLogId) {
        setMessage('Upload staged successfully. Opening review…');
        onUploadComplete(lastLogId);
      } else {
        setMessage('Upload staged successfully.');
      }

      setUploadedFiles([]);
      setUploadErrors([]);
      setShowErrorBox(false);
    } catch (e) {
      console.error('Upload failed with error:', e);
      // Only show generic message if we haven't already handled validation errors
      if (!hasErrors) {
        setMessage(e?.message || 'Upload failed');
        setUploadErrors([]);
        setShowErrorBox(false);
      } else {
        // If hasErrors is true, the error box should already be showing
        // Just ensure it's displayed
        if (validationErrors.length > 0) {
          setUploadErrors(validationErrors);
          setShowErrorBox(true);
        }
      }
    } finally {
      setBusy(false);
    }
  }, [uploadedFiles, onUploadComplete, selectedVendor, catalogUploadMode]);

  const vendorExample = vendorExamples[selectedVendor];
  const showInfoBox = !!vendorExample;

  return (
    <PageContainer>
      <ContentContainer fileUploaded={uploadedFiles.length > 0} activeTab={activeTab}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: -0.02 }}>
            Upload catalog
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Map your vendor file, choose partial or full replacement, then drop CSV or Excel. Staged items are
            reviewed before they are written to products.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 320px) 1fr' },
              gap: { xs: 2, md: 3 },
              alignItems: 'stretch',
            }}
          >
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Step 1
              </Typography>
              <VendorSelection
                fullWidth
                selectedVendor={selectedVendor}
                disabled={uploadedFiles.length > 0}
                onVendorChange={(vendor) => {
                  setSelectedVendor(vendor);
                  setUploadErrors([]);
                  setShowErrorBox(false);
                }}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Catalog mode
              </Typography>
              <RadioGroup
                value={catalogUploadMode}
                onChange={(e) => setCatalogUploadMode(e.target.value)}
              >
                <FormControlLabel
                  value="partial"
                  disabled={uploadedFiles.length > 0}
                  control={<Radio size="small" />}
                  sx={{ alignItems: 'flex-start', ml: 0, mb: 1 }}
                  label={
                    <Box sx={{ opacity: uploadedFiles.length > 0 ? 0.5 : 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Partial update
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Only products in this file are added or updated. Everything else for the vendor stays as-is.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="full_replace"
                  disabled={uploadedFiles.length > 0}
                  control={<Radio size="small" />}
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                  label={
                    <Box sx={{ opacity: uploadedFiles.length > 0 ? 0.5 : 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Full catalog replacement
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        After a successful import, active products for this vendor that are not in the file get stock
                        set to 0 (hidden on the storefront). Use only with a complete vendor file.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 180 }}>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Step 2
              </Typography>
              {uploadedFiles.length === 0 ? (
                <>
                  <DragAndDrop onFileUpload={handleFileUpload} multiple disabled={!selectedVendor} />
                  {!selectedVendor && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Select a vendor to enable the drop zone.
                    </Typography>
                  )}
                </>
              ) : (
                <FullHeightCardWrapper sx={{ flex: 1, maxHeight: 400, overflowY: 'auto' }}>
                  {uploadedFiles.map((file, index) => (
                    <FileUploadCard
                      key={index}
                      file={file}
                      onRemove={() => handleRemoveFile(index)}
                      onCompleteUpload={handleCompleteUpload}
                    />
                  ))}
                </FullHeightCardWrapper>
              )}
            </Box>
          </Box>
        </Paper>

        {showInfoBox && uploadedFiles.length === 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Alert
              icon={<InfoIcon />}
              severity="info"
              sx={{
                backgroundColor: '#e8f4fc',
                '& .MuiAlert-icon': { color: '#1565c0' },
              }}
            >
              <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
                Template reference — {selectedVendor}
              </AlertTitle>
              
              {/* Excel Format Table */}
              <Paper elevation={0} sx={{ mt: 2, mb: 2, overflow: 'auto', backgroundColor: '#fafafa' }}>
                <Table size="small" sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#fff3cd' }}>
                      {vendorExample.headers.map((header, idx) => (
                        <TableCell 
                          key={idx} 
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.75rem',
                            padding: '8px 4px',
                            border: '1px solid #ddd',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {vendorExample.example.map((cell, idx) => (
                        <TableCell 
                          key={idx}
                          sx={{ 
                            fontSize: '0.75rem',
                            padding: '8px 4px',
                            border: '1px solid #ddd',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={cell}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>

              {/* Notes Section */}
              {vendorExample.notes && vendorExample.notes.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Note:
                  </Typography>
                  <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                    {vendorExample.notes.map((note, idx) => (
                      <li key={idx}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#555' }}>
                          {note}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </Alert>
          </Paper>
        )}

        {/* Error Info Box */}
        {showErrorBox && uploadErrors.length > 0 && (
          <Box sx={{ mb: 0 }}>
            <Alert 
              severity="error"
              sx={{
                backgroundColor: '#ffebee',
                border: '1px solid #ef5350',
                '& .MuiAlert-icon': { color: '#d32f2f' }
              }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setShowErrorBox(false);
                    setUploadErrors([]);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              <AlertTitle sx={{ fontWeight: 600, mb: 2, color: '#c62828' }}>
                Upload Validation Errors ({uploadErrors.length})
              </AlertTitle>
              <Box 
                component="ul" 
                sx={{ 
                  mt: 0, 
                  mb: 0, 
                  pl: 2.5,
                  listStyle: 'disc',
                  '& li': {
                    marginBottom: '8px',
                    lineHeight: 1.6
                  }
                }}
              >
                {uploadErrors.map((error, idx) => {
                  // Format error message in human-readable format
                  let formattedMessage = '';
                  
                  if (error.message) {
                    // If message already exists, use it (it should be in format "Line X: Error Type - Field")
                    formattedMessage = error.message;
                  } else {
                    // Otherwise, construct from line, field, and error type
                    const lineNum = error.line || 'Unknown';
                    const field = error.field || 'Unknown Field';
                    const errorType = error.type || 'Missing Field';
                    formattedMessage = `Line ${lineNum}: ${errorType} - ${field}`;
                  }
                  
                  return (
                    <li key={idx}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#c62828',
                          fontWeight: 500,
                          fontFamily: 'inherit'
                        }}
                      >
                        {formattedMessage}
                      </Typography>
                    </li>
                  );
                })}
              </Box>
            </Alert>
          </Box>
        )}

        {message && (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: showErrorBox ? 'error.main' : 'text.secondary' }}
          >
            {message}
          </Typography>
        )}
      </ContentContainer>
    </PageContainer>
  );
}
